<template>
  <div class="min-h-40">
    <TableLight class="w-full table-fixed">
      <thead>
        <tr>
          <TableH
            class="w-[88px]"
            :label="t('Reviews.product.view.table.date')"
            :sort-params="getSortState(SurveyResponsesSortByEnum.UpdatedAt)"
            @sort="setSort"
          />
          <TableH :label="t('Reviews.product.view.table.campaignName')" />
          <TableH
            :label="t('Reviews.product.view.table.reviewerName')"
            :sort-params="getSortState(SurveyResponsesSortByEnum.ReviewerName)"
            @sort="setSort"
          />
          <TableH
            :label="t('Reviews.product.view.table.reviewerCompany')"
            :sort-params="getSortState(SurveyResponsesSortByEnum.ReviewerCompany)"
            @sort="setSort"
          />
          <TableH :label="t('Reviews.product.view.table.useCase')" />
          <TableH
            class="w-[104px]"
            :label="t('Reviews.product.view.table.recommendationScore')"
            :sort-params="getSortState(SurveyResponsesSortByEnum.RecommendationScore)"
            @sort="setSort"
          />
          <TableH class="w-[140px]" :label="t('Reviews.product.view.table.status')" />
          <TableH class="w-[84px]" label="" />
        </tr>
      </thead>
      <tbody>
        <tr v-if="surveyResponses.length === 0 && !loading">
          <td v-if="filtersDisabled" colspan="8" class="text-center">
            <h3 class="pt-12">{{ t('Reviews.product.view.filters.emptyResultWithoutFiltersTitle') }}</h3>
            <div>{{ t('Reviews.product.view.filters.emptyResultWithoutFiltersText') }}</div>
            <div class="pb-8">
              <RouterLink
                class="btn-outline-color-xs mt-8"
                data-testid="reviews-table-new-campaign"
                :to="{ name: ReviewsRouteName.ReviewsProductCampaignNew, params: { productId: productId } }"
              >
                {{ t('Reviews.product.action.newCampaign') }} <PlusIcon class="lucide-icon-s" />
              </RouterLink>
            </div>
          </td>
          <td v-else colspan="8" class="text-center">
            <h3 class="pt-12">{{ t('Reviews.product.view.filters.emptyResultTitle') }}</h3>
            <div>{{ t('Reviews.product.view.filters.emptyResultText') }}</div>
            <div class="py-8">
              <button class="btn-outline-color-xs mr-2" @click="resetAllFilters">
                {{ t('Reviews.product.view.filters.clearAllButton') }}
              </button>
            </div>
          </td>
        </tr>
        <tr
          v-for="surveyResponse in surveyResponses"
          :key="surveyResponse.id"
          :data-testid="`reviews-table-row-${surveyResponse.id}`"
          :class="{
            'cursor-pointer': isOpenable(surveyResponse),
            'cursor-default select-none': shouldFenceResponse(surveyResponse),
          }"
          class="hover:bg-surface-elevation-2"
          @click="openDetails(surveyResponse)"
        >
          <td class="text-left">
            <FencedCell v-if="shouldFenceResponse(surveyResponse)" cell-type="date" />
            <span v-else>{{ formatDate(surveyResponse.updatedAt) }}</span>
          </td>
          <td class="truncate text-left">
            <FencedCell v-if="shouldFenceResponse(surveyResponse)" cell-type="campaign" />
            <span v-else>{{ getCampaignName(surveyResponse) }}</span>
          </td>
          <td class="truncate text-left">
            <FencedCell v-if="shouldFenceResponse(surveyResponse)" cell-type="name" />
            <span v-else>{{ surveyResponse.reviewerFirstName || '---' }}</span>
          </td>
          <td class="text-left break-words">
            <FencedCell v-if="shouldFenceResponse(surveyResponse)" cell-type="company" />
            <span v-else>{{ surveyResponse.reviewerCompany || '---' }}</span>
          </td>
          <td class="text-left">
            <FencedCell v-if="shouldFenceResponse(surveyResponse)" cell-type="purpose" />
            <ReviewsTableUseCasesCell v-else :use-cases="surveyResponse.purposes" />
          </td>
          <td class="text-left">
            <FencedCell v-if="shouldFenceResponse(surveyResponse)" cell-type="rating" />
            <div v-else class="flex items-center">
              <RatingStars :value="ratingValue(surveyResponse)" dense />
              <span class="ml-1">{{ ratingValueText(surveyResponse) }}</span>
            </div>
          </td>
          <td class="text-left">
            <SurveyResponseStatusBadge :survey-response="surveyResponse" />
          </td>
          <td class="text-left">
            <div class="flex flex-nowrap justify-center gap-x-4">
              <ReviewInteractionStatus
                subject="product"
                :type="
                  surveyResponse.reminders.length > 0 && surveyResponse.aasmState !== SurveyResponsesStateEnum.Published
                    ? ReviewInteractionType.REMINDER_CHECK
                    : ReviewInteractionType.REMINDER
                "
                :tooltip-text="reminderToolTipMessage(surveyResponse)"
              />
              <ReviewInteractionStatus
                subject="product"
                :type="hasReviewReply(surveyResponse) ? ReviewInteractionType.REPLY_CHECK : ReviewInteractionType.REPLY"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </TableLight>
    <div v-if="loading" class="mt-6 flex justify-center">
      <CircleSpinner />
    </div>
  </div>
</template>

<script setup lang="ts">
import { CircleSpinner, RatingStars } from '@ramp106/omrjs-core-ui'
import { PlusIcon } from '@ramp106/omrjs-core-ui/icons'
import { differenceInDays } from 'date-fns'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import ReviewInteractionStatus from '@/components/ReviewsCommon/ReviewInteractionStatus/ReviewInteractionStatus.vue'
import { ReviewInteractionType } from '@/components/ReviewsCommon/ReviewInteractionStatus/types'
import type { SurveyResponse } from '@/components/ReviewsManagement/Reviews/types'
import { SurveyResponseState, useSurveyResponseState } from '@/composables/useSurveyResponseState'
import { SortDirectionEnum, SurveyResponsesSortByEnum, SurveyResponsesStateEnum, SurveyResponsesViewEnum } from '@/gql/reviews'
import { ReviewsRouteName } from '@/router/types'
import { TableH, TableLight, useSortQuery } from '@/ui/TableLight'

import FencedCell from './FencedCell.vue'
import ReviewsTableUseCasesCell from './ReviewsTableUseCasesCell.vue'
import SurveyResponseStatusBadge from './SurveyResponseStatusBadge.vue'
import { useFormatTableDate } from './useFormatTableDate'
import { useRouterQueryFilters } from './useRouterQueryReviewsFilters'

const MAX_REMINDERS_COUNT = 3
const REMINDER_RESEND_DAYS = 45
const props = defineProps<{
  productId: string
  hasPaidPlan: boolean
  surveyResponses: SurveyResponse[]
  loading?: boolean
  filtersDisabled?: boolean
  viewState: SurveyResponsesViewEnum
}>()

const { t } = useI18n()
const router = useRouter()
const { setSort, getSortState } = useSortQuery(SurveyResponsesSortByEnum, SortDirectionEnum)
const { resetAllFilters } = useRouterQueryFilters()
const formatDate = useFormatTableDate()

function getDaysSinceLastReminder(surveyResponse: SurveyResponse) {
  if (surveyResponse.reminders.length > 0) {
    const lastReminderSent = surveyResponse.reminders[surveyResponse.reminders.length - 1].sentAt || ''
    return differenceInDays(new Date(lastReminderSent), new Date()) + REMINDER_RESEND_DAYS
  }
  return 0
}

function reminderToolTipMessage(surveyResponse: SurveyResponse) {
  if (surveyResponse.reminders.length > MAX_REMINDERS_COUNT) {
    return t('Reviews.product.review.maxReminderSendStatusHint', { count: surveyResponse.reminders.length })
  }
  switch (props.viewState) {
    case 'outdated':
      if (surveyResponse && getDaysSinceLastReminder(surveyResponse) > 0) {
        return t('Reviews.product.review.outdatedReminderStatusHint', { days: getDaysSinceLastReminder(surveyResponse) })
      } else {
        return t('Reviews.product.review.reminderStatusHint', { count: surveyResponse.reminders.length })
      }
    default:
      return t('Reviews.product.review.reminderStatusHint', { count: surveyResponse.reminders.length })
  }
}

function hasReviewReply(surveyResponse: SurveyResponse) {
  return !!surveyResponse.review?.reviewReply
}

function isOpenable(surveyResponse: SurveyResponse) {
  const status = surveyResponse.aasmState
  const statusIsOpenable = [
    SurveyResponsesStateEnum.Published,
    SurveyResponsesStateEnum.Rejected,
    SurveyResponsesStateEnum.Drafted,
    SurveyResponsesStateEnum.Proposed,
    SurveyResponsesStateEnum.Stale,
  ].includes(status as SurveyResponsesStateEnum)

  if (!statusIsOpenable) {
    return false
  }

  return !shouldFenceResponse(surveyResponse)
}

function ratingValue(surveyResponse: SurveyResponse) {
  const score = surveyResponse.review?.recommendationScore
  if (typeof score !== 'number') {
    return undefined
  }
  return score / 2
}

function ratingValueText(surveyResponse: SurveyResponse) {
  return ratingValue(surveyResponse)?.toFixed(1) || '-'
}

function openDetails(surveyResponse: SurveyResponse) {
  if (!isOpenable(surveyResponse)) {
    return
  }

  router.push({ name: ReviewsRouteName.ReviewsProductReviewDetails, params: { surveyResponseId: surveyResponse.id } })
}

function shouldFenceResponse(surveyResponse: SurveyResponse): boolean {
  if (!props.hasPaidPlan) {
    const responseState = useSurveyResponseState(surveyResponse).value
    return responseState !== SurveyResponseState.Published
  }
  return false
}

function getCampaignName(surveyResponse: SurveyResponse): string {
  if (surveyResponse.review?.campaign?.title) {
    return surveyResponse.review.campaign.title
  } else if (surveyResponse.requestedCampaign?.title) {
    return surveyResponse.requestedCampaign.title
  }
  return '---'
}
</script>
